import { useState, useEffect } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  NumberField,
  BooleanField,
  useRecordContext,
  TopToolbar,
  FunctionField,
  Button,
  useRefresh,
  useNotify,
  useDataProvider,
  Loading,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';

const EventShowActions = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  if (!record) return null;

  const handleCancel = async () => {
    if (!window.confirm(`Cancel event #${record.event_id}?`)) return;

    try {
      await dataProvider.create(`events/${record.event_id}/cancel`, { data: {} });
      notify('Event cancelled successfully', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(error.message || 'Failed to cancel event', { type: 'error' });
    }
  };

  const handleForceEnd = async () => {
    if (!window.confirm(`Force end event #${record.event_id}?`)) return;

    try {
      await dataProvider.create(`events/${record.event_id}/force-end`, { data: {} });
      notify('Event force ended successfully', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(error.message || 'Failed to force end event', { type: 'error' });
    }
  };

  const handleRecalculate = async () => {
    if (!window.confirm(`Recalculate ranks for event #${record.event_id}?`)) return;

    try {
      await dataProvider.create(`events/${record.event_id}/recalculate`, { data: {} });
      notify('Ranks recalculated successfully', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(error.message || 'Failed to recalculate ranks', { type: 'error' });
    }
  };

  const canCancel = record.status === 'scheduled' || record.status === 'active';
  const canForceEnd = record.status === 'active';
  const canRecalculate = record.status === 'active' || record.status === 'ended';

  return (
    <TopToolbar>
      {canRecalculate && (
        <Button
          label="Recalculate Ranks"
          onClick={handleRecalculate}
          startIcon={<RefreshIcon />}
        />
      )}
      {canForceEnd && (
        <Button
          label="Force End"
          onClick={handleForceEnd}
          startIcon={<StopIcon />}
          color="warning"
        />
      )}
      {canCancel && (
        <Button
          label="Cancel Event"
          onClick={handleCancel}
          startIcon={<CancelIcon />}
          color="error"
        />
      )}
    </TopToolbar>
  );
};

export const EventShow = () => {
  return (
    <Show actions={<EventShowActions />}>
      <SimpleShowLayout>
        <EventHeader />
        <EventDetails />
        <PrizePoolDetails />
        <EventParticipants />
        <EscrowDetails />
      </SimpleShowLayout>
    </Show>
  );
};

const EventHeader = () => {
  const record = useRecordContext();

  if (!record) return null;

  let statusColor: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' = 'default';
  switch (record.status) {
    case 'scheduled':
      statusColor = 'info';
      break;
    case 'active':
      statusColor = 'success';
      break;
    case 'ended':
      statusColor = 'default';
      break;
    case 'cancelled':
      statusColor = 'error';
      break;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h5">Event #{record.event_id}</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              📊 Chat: <strong>{record.chat_title}</strong>
              {record.chat_username && ` (${record.chat_username})`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              👤 Owner: {record.owner_username} ({record.owner_first_name || 'N/A'})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              👥 Participants: {record.participant_count || 0}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Chip label={record.status} color={statusColor} />
              {record.payment_status && (
                <Chip
                  label={`Payment: ${record.payment_status}`}
                  color={record.payment_status === 'PAID' ? 'success' : 'warning'}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const EventDetails = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Event Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Start Time
            </Typography>
            <Typography variant="body1">
              {new Date(record.start_time).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              End Time
            </Typography>
            <Typography variant="body1">
              {record.end_time ? new Date(record.end_time).toLocaleString() : 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {new Date(record.created_at).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Top Positions
            </Typography>
            <Typography variant="body1">{record.top_positions}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const PrizePoolDetails = () => {
  const record = useRecordContext();

  if (!record) return null;

  const referralPool =
    (record.actual_referral_turnover || record.estimated_referral_turnover || 0) *
    (record.referral_share_percent || 0) /
    100 *
    0.01;
  const additionalPool = record.additional_pool_amount || 0;
  const totalPool = referralPool + additionalPool;
  const currency = record.additional_pool_currency || 'TON';

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Prize Pool
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Referral Pool
            </Typography>
            <Typography variant="h6" color="primary.main">
              {referralPool.toFixed(4)} {currency}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {record.referral_share_percent}% of {record.actual_referral_turnover > 0 ? 'actual' : 'estimated'} turnover
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Additional Pool
            </Typography>
            <Typography variant="h6" color="primary.main">
              {additionalPool.toFixed(4)} {currency}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Total Pool
            </Typography>
            <Typography variant="h6" color="success.main">
              {totalPool.toFixed(4)} {currency}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Commission: {record.commission_rate || 5}%
            </Typography>
          </Grid>
          {record.random_draw_enabled && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Random Draw
                </Typography>
                <Typography variant="body1">
                  🎲 {record.random_draw_percent}% ({(totalPool * record.random_draw_percent / 100).toFixed(4)} {currency})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Min points: {record.random_draw_min_points}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Distribution Preset
                </Typography>
                <Typography variant="body1">{record.distribution_preset || 'BALANCED'}</Typography>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const EventParticipants = () => {
  const record = useRecordContext();
  const [participants, setParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!record || !record.event_id) return;

    const fetchParticipants = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/events/${record.event_id}/leaderboard?limit=50`);

        if (!response.ok) {
          throw new Error('Failed to fetch participants');
        }

        const data = await response.json();
        setParticipants(data.leaderboard || []);
        setError(null);
      } catch (err: any) {
        setError(err.message);
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [record]);

  if (!record) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrophyIcon color="primary" />
          <Typography variant="h6">
            Participants & Leaderboard
          </Typography>
          {record.participant_count > 0 && (
            <Chip
              label={`${record.participant_count} total`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {loading && <Loading />}

        {error && (
          <Typography variant="body2" color="error">
            Failed to load participants: {error}
          </Typography>
        )}

        {!loading && !error && participants.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No participants yet. Waiting for first bets...
          </Typography>
        )}

        {!loading && !error && participants.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Rank</strong></TableCell>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell align="right"><strong>Points</strong></TableCell>
                  <TableCell align="right"><strong>Bets</strong></TableCell>
                  <TableCell align="right"><strong>Volume (USD)</strong></TableCell>
                  <TableCell align="right"><strong>Wins (USD)</strong></TableCell>
                  <TableCell align="right"><strong>Prize</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {participants.map((participant) => (
                  <TableRow
                    key={participant.user_id}
                    sx={{
                      backgroundColor: participant.rank <= 3 ? 'rgba(255, 215, 0, 0.1)' : 'inherit',
                    }}
                  >
                    <TableCell>
                      {participant.rank === 1 && '🥇 '}
                      {participant.rank === 2 && '🥈 '}
                      {participant.rank === 3 && '🥉 '}
                      #{participant.rank}
                    </TableCell>
                    <TableCell>
                      {participant.username || `User ${participant.user_id}`}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{Number(participant.points || 0).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell align="right">{participant.bet_count}</TableCell>
                    <TableCell align="right">
                      ${Number(participant.total_bet_usd || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      ${Number(participant.total_win_usd || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {Number(participant.prize_amount || 0) > 0 ? (
                        <Chip
                          label={`${Number(participant.prize_amount).toFixed(4)} ${participant.prize_currency}`}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <span>-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {participants.length >= 50 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing top 50 participants. Total: {record.participant_count}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const EscrowDetails = () => {
  const record = useRecordContext();

  if (!record) return null;
  if (!record.invoice_id) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Escrow Payment
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Invoice ID
            </Typography>
            <Typography variant="body1">{record.invoice_id}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Invoice Amount
            </Typography>
            <Typography variant="body1">
              {record.invoice_amount} {record.invoice_currency}
            </Typography>
          </Grid>
          {record.escrow_paid_at && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Paid At
              </Typography>
              <Typography variant="body1">
                {new Date(record.escrow_paid_at).toLocaleString()}
              </Typography>
            </Grid>
          )}
          {record.invoice_url && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Invoice URL
              </Typography>
              <Typography variant="body1">
                <a href={record.invoice_url} target="_blank" rel="noopener noreferrer">
                  {record.invoice_url}
                </a>
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
